import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import Modal from "@/components/Modal";

/**
 * Sprint 5.5 — "Where is this product used?" modal.
 * Surfaces order count + 10 most recent orders for a given product.
 * Used before archiving or repricing a product so the operator knows the impact.
 */
export default function WhereUsedModal({ open, product, onClose }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !product?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/financial-products/${product.id}/usage`,
          { withCredentials: true }
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, product?.id]);

  return (
    <Modal
      open={open}
      onClose={() => { setData(null); onClose(); }}
      title={t("where_used_title")}
      testId={DASHBOARD.productWhereUsedModal}
    >
      <div className="space-y-4">
        <div className="border-l-4 border-black bg-[var(--surface-alt)] p-3">
          <div className="font-display font-bold text-lg">{product?.name}</div>
          {product?.sku ? (
            <div className="font-mono-tech text-xs text-[var(--text-secondary)] mt-0.5">
              SKU: {product.sku}
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="text-sm text-[var(--text-secondary)]">…</div>
        ) : data ? (
          <>
            <div
              data-testid={DASHBOARD.productWhereUsedCount}
              className="text-center bg-white border-2 border-black p-4"
            >
              <div className="font-display font-bold text-4xl tabular-nums">
                {data.orders_count}
              </div>
              <div className="text-xs uppercase tracking-widest text-[var(--text-secondary)] mt-1">
                {t("where_used_orders_count")}
              </div>
            </div>

            {data.orders_count === 0 ? (
              <div className="text-sm text-[var(--text-secondary)]">
                {t("where_used_no_orders")}
              </div>
            ) : (
              <div>
                <div className="label-overline mb-2">{t("where_used_recent")}</div>
                <div className="border border-[var(--border)] divide-y divide-[var(--border)] max-h-64 overflow-y-auto">
                  {data.recent_orders.map((o) => (
                    <div key={o.id} className="p-2 flex justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <div className="font-display font-bold truncate">
                          {o.invoice_number ? `#${o.invoice_number}` : (o.id?.slice(0, 8) || "—")}
                          {" · "}
                          {o.company_name || o.customer_name || "—"}
                        </div>
                        <div className="text-[11px] font-mono-tech text-[var(--text-secondary)] truncate">
                          {o.customer_name && o.company_name && o.customer_name !== o.company_name
                            ? `${o.customer_name} · `
                            : ""}
                          {o.created_at ? o.created_at.slice(0, 10) : ""}
                          {o.status ? ` · ${o.status}` : ""}
                        </div>
                      </div>
                      <div className="font-mono-tech tabular-nums text-sm whitespace-nowrap">
                        {typeof o.total_amount === "number"
                          ? `$${o.total_amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : null}

        <div className="flex justify-end pt-2">
          <button
            type="button"
            data-testid={DASHBOARD.productWhereUsedClose}
            onClick={() => { setData(null); onClose(); }}
            className="border border-black px-4 py-2 font-display font-bold text-sm hover:bg-black hover:text-white transition-colors"
          >
            {t("csv_import_close")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
